import {TextEditorApi, TextEditorSelection} from 'platform/components';

import {
  ComposeMessagePlaceholder,
  ComposeMessageRequest,
  ComposeMessageResponse,
} from '@prompt-bridge/shared/validations';

import {Nullish} from 'shared/web';

import type {ReplyToMessage} from '../SendCustomerEmailForm/types/ReplyToMessage';

export type AiComposerChannel = ComposeMessageRequest['channel'];
export type AiComposerOperation = ComposeMessageRequest['operation'];
export type AiComposerBodyFormat = ComposeMessageRequest['bodyFormat'];
export type AiComposerRefinement = NonNullable<ComposeMessageRequest['refinement']>;
export type AiComposerSource = NonNullable<ComposeMessageRequest['source']>;
export type AiComposerSourceContext = AiComposerSource['context'];
export type AiComposerUsedContext = ComposeMessageResponse['usedContext'];
export type AiComposerUnavailableContext = ComposeMessageResponse['unavailableContext'];
export type AiComposerLanguageSource = ComposeMessageResponse['languageSource'];
/** One variable a template may use; the catalogue grounds a template the way a record grounds a message. */
export type AiComposerPlaceholder = ComposeMessagePlaceholder;

/** The subject/body pair a message surface owns. `subject` is absent on body-only channels. */
export interface AiComposerDraft {
  body: string;
  subject?: string;
}

export type AiComposerApplyMode = 'replace' | 'insertBelow';
export type AiComposerInstructionSource = 'panel' | 'selectionToolbar';

export interface AiComposerSuggestion {
  key: string;
  title: string;
  instruction: string;
  description?: string;
}

export interface AiComposerIdentity {
  context: string;
  recordId: string;
  customerId: string;
  recipientAddress: string;
  replyToMessageId: string;
  body: string;
  subject: string;
}

export interface AiComposerProposal {
  /** A generated message replaces the field, so its placeholders are judged on their own. */
  operation: AiComposerOperation;
  draft: AiComposerDraft;
  usedContext: AiComposerUsedContext;
  unavailableContext: AiComposerUnavailableContext;
  languageSource: AiComposerLanguageSource;
  locale: string | Nullish;
  identity: AiComposerIdentity;
  /** The input whose accepted instruction must be cleared. */
  instructionSource: AiComposerInstructionSource;
  /** Set when the proposal rewrites one selected region instead of the whole message. */
  selection: TextEditorSelection | Nullish;
  /** `true` when the proposal changes template placeholders and must not be applied (AC7). */
  isPlaceholderMismatch: boolean;
}

export interface AiComposerProvenance {
  identity: AiComposerIdentity;
  usedContext: AiComposerUsedContext;
  unavailableContext: AiComposerUnavailableContext;
  languageSource: AiComposerLanguageSource;
  locale: string | Nullish;
}

export interface AiComposerUndoStep {
  previous: AiComposerDraft;
  applied: AiComposerDraft;
}

/** One accepted proposal on the undo stack, with what grounded it. */
export interface AiComposerAppliedStep extends AiComposerUndoStep {
  provenance: AiComposerProvenance;
  /** `true` while the subject is the one the assistant wrote; a later proposal may replace it. */
  hasAiSubject: boolean;
}

export interface AiComposerRecord {
  context: AiComposerSourceContext | Nullish;
  recordId: string | Nullish;
  /** The recipient the employee currently has selected, not the one the form opened with. */
  customerId: string | Nullish;
  /** Local proposal identity only; never sent to the model or the context endpoint. */
  recipientAddress?: string | Nullish;
  replyTo?: ReplyToMessage | Nullish;
}

export type AiComposerEditor = TextEditorApi | Nullish;
